<!-- eslint-disable tailwindcss/no-custom-classname -->
<!-- eslint-disable vue/no-v-html -->
<template>
  <div
    class="relative flex-col typo-subtitle"
    :class="colSpan"
  >
    <h3
      v-if="field?.display"
      class="w-full typo-subtitle"
    >
      {{ field.display }}
    </h3>
    <p v-if="instructions || field.instructions" class="mt-1 text-grey-300 typo-subtitle">
      {{ instructions || field.instructions }}
    </p>

    <ul
      class="flex flex-1 flex-col flex-wrap gap-3"
      :class="field?.display || instructions || field.instructions ? 'mt-1': 'mt-5'"
    >
      <li v-for="(option) in computedOptions" :key="option.label" class="group w-fit">
        <label
          class="relative flex min-h-8 w-fit cursor-pointer select-none items-center pl-11"
          :for="`${idPrefix}${option.value}`"
        >
          <span
            class="block !leading-none typo-subtitle"
            v-html="option.label"
          />

          <Field
            :id="`${idPrefix}${option.value}`"
            :value="option.value"
            :name="name || handle || field.handle"
            :label="label || option.label || field.label || field.display"
            :disabled="disabled"
            :rules="rulesAsString"
            :type="type"
            :model-value="modelValue"
            class="peer absolute left-0 z-0 translate-y-0.5 cursor-pointer opacity-0"
            @update:model-value="$emit('update:model-value', $event)"
          />

          <span class="checkbox-mark" />
          <IconCheck
            class="
              pointer-events-none absolute left-2 top-1.5 size-5 origin-bottom -rotate-45 scale-0 text-white
              opacity-0 transition-all duration-100 ease-in
              peer-checked:rotate-0 peer-checked:scale-100 peer-checked:opacity-100 peer-checked:duration-150
              peer-checked:ease-in-out
            "
          />
        </label>
      </li>
    </ul>

    <!-- ERROR -->
    <FieldError :error="error" />
  </div>
</template>

<!-- eslint-disable-next-line vue/block-lang -->
<script setup>
import { Field } from 'vee-validate'

defineEmits(['update:model-value'])

const props = defineProps({
  options: {
    type: [Array, Object],
    default: () => [],
  },

  handle: {
    type: String,
    default: null,
  },

  type: {
    type: String,
    default: 'checkbox',
  },

  modelValue: {
    type: [String, Number, Boolean, Array],
    default: () => [],
  },

  field: {
    type: Object,
    default: null,
  },

  width: {
    type: [Number, String],
    default: null,
  },

  rules: {
    type: [Array, String],
    default: '',
  },

  disabled: {
    type: Boolean,
    default: false,
  },

  label: {
    type: String,
    default: '',
  },

  name: {
    type: String,
    default: null,
  },

  instructions: {
    type: String,
    default: null,
  },

  error: {
    type: String,
    default: null,
  },
  idPrefix: {
    type: String,
    default: '',
  },
})

const rulesAsString = useFilterVeeValidateRules(props.rules)

const colSpan = computed(() => {
  const width = parseInt(props.width) || props.field?.width || 100

  switch (width) {
    case 25:
      return 'lg:col-span-3 col-span-12'
    case 33:
      return 'lg:col-span-4 col-span-12'
    case 50:
      return 'lg:col-span-6 col-span-12'
    case 66:
      return 'lg:col-span-8 col-span-12'
    default:
      return 'lg:col-span-12 col-span-12'
  }
})

const computedOptions = computed(() => {
  if (Array.isArray(props.options)) {
    return props.options.map((option) => {
      if (typeof option === 'string') {
        return {
          label: option,
          value: option,
        }
      }

      return {
        label: option.value,
        value: option.key,
      }
    })
  }

  return Object.keys(props.options).map(key => ({
    label: props.options[key],
    value: key,
  }))
})
</script>

<style scoped>
.checkbox-mark {
  @apply absolute inset-x-0 top-0 ml-px block size-8 overflow-hidden rounded-sm border-2 border-primary-900 transition-all duration-200 ease-in-out;

  @apply peer-focus-visible:ring-2 peer-focus-visible:ring-offset-2;

  @apply
    peer-checked:bg-primary-900
    peer-checked:duration-300
    peer-checked:ease-out;

  @apply
    peer-checked:after:absolute
    peer-checked:after:-inset-1
    peer-checked:after:bg-gradient-01
    peer-checked:after:opacity-0
    peer-checked:after:transition-all
    peer-checked:after:duration-500
    peer-checked:after:ease-out;

  @apply
    group-hover:border-secondary-900
    group-hover:peer-checked:border-transparent
    group-hover:peer-checked:after:opacity-100
    group-hover:peer-checked:after:ease-out;
}
</style>
